import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box, Paper, Typography, Button, TextField, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Switch, Radio, RadioGroup, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, InputAdornment, Tooltip, Alert, CircularProgress,
} from "@mui/material";
import {
  Refresh as RegenerateIcon,
  CheckCircle as OkIcon,
  CheckCircleOutline as SuccessIcon,
  ErrorOutline as ErrorIcon,
  CloudUploadOutlined as UploadIcon,
  Close as CloseIcon,
  InsertDriveFileOutlined as FileIcon,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  NAVY, cardSx, EmployeeSelect, generateFormNo, getYearOptions, CURRENT_YEAR,
  toKey, fromKey, daysBetween, validateRange, validatePhone, fmtNum,
} from "../../../components/leave/LeaveCommon";
import { fetchHolidays } from "../../../actions/Leave/HolidayAction";
import { fetchLeaveAllocation, clearLeaveAllocation } from "../../../actions/Leave/LeaveAllocationAction";
import { getEmployeeLeaves, findLeaveClash, submitLeaveRequest } from "../../../actions/Leave/LeaveRequestAction";
import { toEmployeeOptions } from "../../../service/Leave/leaveHelpers";

const ACCEPT = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png"];
const MAX_MB = 5;

const toBackendDate = (k) => (k ? k.replace(/-/g, "/") : "");


const SectionTitle = ({ children }) => (
  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", letterSpacing: 1.2, textTransform: "uppercase", mb: 1.5 }}>
    {children}
  </Typography>
);

//File Upload
const FileUpload = ({ value, onChange }) => {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [err, setErr] = useState("");

  const pick = (file) => {
    setErr("");
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) return setErr(`File size exceeds ${MAX_MB}MB limit.`);
    if (!ACCEPT.some((ext) => file.name.toLowerCase().endsWith(ext)))
      return setErr(`Invalid file type. Accepted: ${ACCEPT.join(", ")}`);
    const reader = new FileReader();
    reader.onload = (e) => onChange({ name: file.name, size: file.size, data: e.target.result });
    reader.onerror = () => setErr("Failed to read file.");
    reader.readAsDataURL(file);
  };

  const size = (b) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`);

  return (
    <Box>
      {value ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1.5, border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: "#f8fafc" }}>
          <FileIcon sx={{ color: "#154c79" }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>{value.name}</Typography>
            <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{size(value.size)}</Typography>
          </Box>
          <Tooltip title="Remove file">
            <IconButton size="small" onClick={() => onChange(null)}><CloseIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); pick(e.dataTransfer.files[0]); }}
          sx={{
            p: 2.5, textAlign: "center", cursor: "pointer", borderRadius: "8px",
            border: `1.5px dashed ${drag ? NAVY : "#cbd5e1"}`,
            backgroundColor: drag ? "#eff6ff" : "#f8fafc",
            "&:hover": { borderColor: NAVY },
          }}
        >
          <UploadIcon sx={{ color: "#94a3b8" }} />
          <Typography sx={{ fontSize: 13, color: "#475569" }}>Click or drag to upload attachment</Typography>
          <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
            Accepted: {ACCEPT.join(", ")} (max {MAX_MB}MB)
          </Typography>
          <input ref={inputRef} type="file" hidden accept={ACCEPT.join(",")} onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} />
        </Box>
      )}
      {err && <Typography sx={{ fontSize: 12, color: "#d32f2f", mt: 0.75 }}>{err}</Typography>}
    </Box>
  );
};

//Page
const emptyForm = () => {
  const today = toKey(new Date());
  return {
    formNo: generateFormNo(), serviceNo: "", year: String(CURRENT_YEAR),
    leaveType: "", days: "1", start: today, end: today,
    half: false, session: "FIRST_HALF", contact: "", reason: "", attachment: null,
  };
};

const LeaveRequestTab = ({ onSubmitted }) => {
  const dispatch = useDispatch();

  // ── Redux data ──────────────────────────────────────────────
  const { data: rawEmployees } = useSelector((s) => s.getAllEmployees) || {};
  const { data: leaveTypes = [] } = useSelector((s) => s.leaveTypes) || {};
  const { data: allocations = [], loading: typesLoading } = useSelector((s) => s.leaveAllocations) || {};
  const { data: holidays = [] } = useSelector((s) => s.leaveHolidays) || {};
  const userId = useSelector((s) => s.auth?.serviceNo);

  const [form, setForm] = useState(emptyForm);
  const [existing, setExisting] = useState([]); // this employee's earlier requests
  const [dialog, setDialog] = useState({ open: false, ok: true, message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (patch) =>
    setForm((f) => {
      const next = { ...f, ...patch };
      if (patch.days !== undefined && patch.half === undefined && Number(patch.days) === 0.5) {
        next.half = true;
      }
      if (next.half) {
        next.end = next.start;
        next.days = "0.5";
      } else if (patch.start !== undefined || patch.end !== undefined || patch.half !== undefined) {
        if (next.end < next.start) next.end = next.start;
        next.days = String(daysBetween(next.start, next.end) || 1);
      }
      return next;
    });

  const employees = useMemo(() => toEmployeeOptions(rawEmployees), [rawEmployees]);
  const employee = employees.find((e) => e.serviceNo === form.serviceNo) || null;
  const typeNameById = useMemo(() => Object.fromEntries(leaveTypes.map((t) => [t.id, t.name])), [leaveTypes]);
  const holidayDates = useMemo(() => holidays.filter((h) => h.active).map((h) => h.date), [holidays]);

  // holidays for the date validation
  useEffect(() => { dispatch(fetchHolidays()); }, [dispatch]);

  // allocation (leave types + balance) and earlier requests of the picked employee
  useEffect(() => {
    if (!form.serviceNo) {
      dispatch(clearLeaveAllocation());
      setExisting([]);
      return undefined;
    }
    let cancelled = false;
    dispatch(fetchLeaveAllocation(form.serviceNo));
    dispatch(getEmployeeLeaves(form.serviceNo))
      .then((list) => { if (!cancelled) setExisting(list); })
      .catch((e) => console.error("Failed to load earlier requests:", e));
    return () => { cancelled = true; };
  }, [form.serviceNo, dispatch]);

  // Leave types this employee has in the chosen year, with remaining balance
  const typeOptions = useMemo(() => {
    const map = new Map();
    // Leave types that exist in the list but are set to Inactive are not offered
    const inactiveTypeIds = new Set(leaveTypes.filter((t) => t.isActive === false).map((t) => t.id));
    allocations
      .filter((a) => a.typeId && a.year === form.year && !inactiveTypeIds.has(a.typeId))
      .forEach((a) => {
        if (map.has(a.typeId)) return;
        const name = a.typeName || typeNameById[a.typeId] || `Leave Type ${a.typeId}`;
        map.set(a.typeId, {
          code: a.typeId, name, balance: a.balance,
          label: `${name} (${fmtNum(a.balance)} day${a.balance === 1 ? "" : "s"} left)`,
        });
      });
    return Array.from(map.values());
  }, [allocations, form.year, typeNameById, leaveTypes]);

  const selectedType = typeOptions.find((t) => t.code === form.leaveType) || null;

  const rangeError = useMemo(
    () =>
      form.start && form.start < toKey(new Date())
        ? "Leave cannot be requested for past dates"
        : validateRange(form.start, form.end, holidayDates),
    [form.start, form.end, holidayDates]
  );

  const daysError = useMemo(() => {
    if (form.half) return "";
    if (form.days === "") return "Leave Days is required";
    const n = Number(form.days);
    if (Number.isNaN(n)) return "Leave Days must be a number";
    if (n <= 0) return "Leave Days must be greater than 0";
    if (n < 1) return "Less than 1 day must be requested as a Half Day. Turn on the Half Day toggle.";
    if (selectedType && selectedType.balance !== null && n > selectedType.balance)
      return `Only ${fmtNum(selectedType.balance)} day${selectedType.balance === 1 ? "" : "s"} available for ${selectedType.name}`;
    return "";
  }, [form.days, form.half, selectedType]);

  const phoneError = validatePhone(form.contact);

  const dupFormNo = useMemo(
    () => !!form.formNo.trim() && existing.some((l) => l.formNo === form.formNo.trim()),
    [form.formNo, existing]
  );

  const onEmployee = (emp) => {
    if (!emp) return set({ serviceNo: "", contact: "", leaveType: "", attachment: null });
    set({ serviceNo: emp.serviceNo, leaveType: "", contact: "", attachment: null });
  };

  const onYear = (year) => set({ year, leaveType: "" });

  // Pick the first usable leave type automatically (like DGES)
  useEffect(() => {
    if (!employee) return;
    if (!typeOptions.some((t) => t.code === form.leaveType)) {
      const first = typeOptions.find((t) => t.balance === null || t.balance > 0);
      setForm((f) => ({ ...f, leaveType: first ? first.code : "" }));
    }
  }, [employee, typeOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const fail = (message) => setDialog({ open: true, ok: false, message });

  const reset = () => setForm(emptyForm());

  const submit = async () => {
    if (!form.formNo.trim()) return fail("Leave Form No. required");
    if (!form.serviceNo) return fail("Employee required");
    if (!form.leaveType) return fail("Leave type required");
    if (!form.start) return fail("Start date required");
    if (!form.end) return fail("End date required");
    if (form.end < form.start) return fail("End date must be after start date");
    if (form.half && !form.session) return fail("Select First Half or Second Half");
    if (daysError) return fail(daysError);
    if (phoneError) return fail(phoneError);
    if (!userId) return fail("Unable to determine logged-in user. Please log in again.");
    if (rangeError) return fail(rangeError);
    if (!form.reason.trim()) return fail("Reason required");

    setSubmitting(true);

    // fresh duplicate check right before submitting (Form No, then overlapping dates)
    try {
      const fresh = await dispatch(getEmployeeLeaves(form.serviceNo));
      const clash = findLeaveClash(fresh, { formNo: form.formNo.trim(), start: form.start, end: form.end });
      if (clash) {
        setSubmitting(false);
        return fail(clash.message);
      }
    } catch (e) {
      console.error("Duplicate check failed:", e); // DGES carries on when the check itself fails
    }

    const res = await dispatch(
      submitLeaveRequest({
        empServiceNo: form.serviceNo,
        formNo: form.formNo.trim(),
        leaveType: form.leaveType,
        fromDate: toBackendDate(form.start),
        toDate: toBackendDate(form.end),
        isHalfDay: form.half ? "Y" : "N",
        halfDayType: form.half ? form.session : "",
        reason: form.reason.trim(),
        contactNo: form.contact.trim() || "-",
        attachmentPath: form.attachment?.data || "",
        userId,
      })
    );
    setSubmitting(false);
    setDialog({ open: true, ok: res.ok, message: res.message });
  };

  const closeDialog = () => {
    const wasOk = dialog.ok;
    setDialog({ ...dialog, open: false });
    if (wasOk) {
      reset();
      onSubmitted();
    }
  };

  const dateField = (label, key, disabled) => (
    <DatePicker
      label={label}
      value={form[key] ? fromKey(form[key]) : null}
      onChange={(d) => set({ [key]: d && !isNaN(d.getTime()) ? toKey(d) : "" })}
      format="yyyy/MM/dd"
      disabled={disabled}
      minDate={fromKey(key === "end" && form.start > toKey(new Date()) ? form.start : toKey(new Date()))}
      slotProps={{ textField: { size: "small", fullWidth: true, required: true } }}
    />
  );

  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 3 }}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: "grid", gap: 4, gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(2, minmax(0, 1fr))" } }}>
          {/* Left Column */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box>
              <SectionTitle>Employee</SectionTitle>
              <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
                <EmployeeSelect
                  required label="Select Employee" options={employees}
                  value={employee} onChange={onEmployee}
                />
                <TextField
                  size="small" label="Service No." value={form.serviceNo}
                  placeholder="Auto-filled on selection" InputProps={{ readOnly: true }}
                  sx={{ "& .MuiInputBase-root": { backgroundColor: "#f1f5f9" } }}
                />
                <TextField
                  size="small" label="Contact No." value={form.contact} placeholder="e.g. 0771234567"
                  onChange={(e) => set({ contact: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                  error={!!phoneError} helperText={phoneError}
                />
              </Box>
            </Box>

            <Box>
              <SectionTitle>Leave Identification</SectionTitle>
              <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
                <TextField
                  size="small" required label="Leave Form No." value={form.formNo}
                  onChange={(e) => set({ formNo: e.target.value })}
                  error={dupFormNo}
                  helperText={
                    dupFormNo ? `Leave Form No "${form.formNo}" already exists.`
                      : form.serviceNo && form.formNo ? "Form No is available" : " "
                  }
                  FormHelperTextProps={{ sx: { color: dupFormNo ? undefined : "#059669" } }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Regenerate Form Number">
                          <IconButton size="small" onClick={() => set({ formNo: generateFormNo() })}>
                            <RegenerateIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!dupFormNo && form.serviceNo && form.formNo && <OkIcon fontSize="small" sx={{ color: "#10b981", ml: 0.5 }} />}
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small">
                  <InputLabel>Leave Year</InputLabel>
                  <Select value={form.year} label="Leave Year" onChange={(e) => onYear(e.target.value)}>
                    {getYearOptions().map((y) => <MenuItem key={y} value={String(y)}>{y}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box>
              <SectionTitle>Attachment</SectionTitle>
              <FileUpload value={form.attachment} onChange={(attachment) => set({ attachment })} />
            </Box>
          </Box>

          {/* Right Column */}
          <Box>
            <SectionTitle>Leave Details</SectionTitle>
            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
              <FormControl size="small" required disabled={!employee}>
                <InputLabel>Leave Type</InputLabel>
                <Select value={form.leaveType} label="Leave Type" onChange={(e) => set({ leaveType: e.target.value })}>
                  {typeOptions.map((t) => (
                    <MenuItem key={t.code} value={t.code} disabled={t.balance !== null && t.balance <= 0}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small" required label="Leave Days" type="number"
                inputProps={{ step: 0.5, min: 0.5 }}
                value={form.days}
                onChange={(e) => set({ days: e.target.value })}
                disabled={form.half}
                error={!!daysError} helperText={daysError || " "}
              />
            </Box>

            {!employee ? (
              <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>Please select an employee first</Alert>
            ) : typesLoading ? (
              <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>Loading leave types...</Alert>
            ) : selectedType && selectedType.balance !== null ? (
              <Alert severity={selectedType.balance <= 0 ? "error" : "success"} sx={{ mb: 2, fontSize: 12 }}>
                {selectedType.name}: {fmtNum(selectedType.balance)} day{selectedType.balance === 1 ? "" : "s"} remaining
                {selectedType.balance <= 0 ? " — no balance left" : ""}
              </Alert>
            ) : !typeOptions.length ? (
              <Alert severity="warning" sx={{ mb: 2, fontSize: 12 }}>No leave types available for this employee in {form.year}</Alert>
            ) : null}

            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(2, minmax(0, 1fr))", mb: 2 }}>
              {dateField("Start Date", "start", false)}
              {dateField("End Date", "end", form.half)}
            </Box>

            {rangeError && <Alert severity="error" sx={{ mb: 2, fontSize: 12 }}>{rangeError}</Alert>}

            <Box sx={{ p: 1.5, mb: 2, border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: "#f8fafc" }}>
              <FormControlLabel
                control={<Switch checked={form.half} onChange={(e) => set({ half: e.target.checked })} color="primary" />}
                label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Is Half Day</Typography>}
              />
              {form.half && (
                <RadioGroup row value={form.session} onChange={(e) => set({ session: e.target.value })} sx={{ ml: 1 }}>
                  <FormControlLabel value="FIRST_HALF" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>First Half</Typography>} />
                  <FormControlLabel value="SECOND_HALF" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 13 }}>Second Half</Typography>} />
                </RadioGroup>
              )}
            </Box>

            <TextField
              label="Reason" size="small" fullWidth multiline minRows={5} placeholder="Reason for leave..." required
              value={form.reason} onChange={(e) => set({ reason: e.target.value })}
            />
          </Box>
        </Box>
      </LocalizationProvider>

      {/* Actions */}
      <Box sx={{ display: "flex", gap: 1.5, mt: 3 }}>
        <Button onClick={reset} sx={{ color: "#64748b", border: "1px solid #e2e8f0", textTransform: "none", fontWeight: 600, px: 3 }}>
          Cancel
        </Button>
        <Button
          variant="contained" onClick={submit} disabled={submitting || dupFormNo || !!rangeError}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ backgroundColor: NAVY, textTransform: "none", fontWeight: 700, px: 4, "&:hover": { backgroundColor: "#0e3c66" } }}
        >
          Submit Leave Request
        </Button>
      </Box>

      {/* Result Dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: "center", pt: 3 }}>
          {dialog.ok
            ? <SuccessIcon sx={{ fontSize: 56, color: "#10b981" }} />
            : <ErrorIcon sx={{ fontSize: 56, color: "#ef4444" }} />}
          <Typography sx={{ fontWeight: 700, color: NAVY, fontSize: 20 }}>{dialog.ok ? "Success!" : "Error"}</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ textAlign: "center", color: "#64748b", fontSize: 14 }}>{dialog.message}</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained" onClick={closeDialog}
            sx={{ backgroundColor: dialog.ok ? NAVY : "#dc2626", textTransform: "none", fontWeight: 600, px: 4 }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default LeaveRequestTab;